/** @jsxImportSource @emotion/react */
import React from 'react';
import ReactDOM from 'react-dom/client';
import tw from 'twin.macro';
import { StyleSheetManager, createGlobalStyle } from 'styled-components';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { defaultConfig } from './store/constants';
import useWidgetStore from './store/useWidgetStore';
import globalStyles from './styles/global.css?raw';
import scrollregionStyles from 'overlayscrollbars/overlayscrollbars.css?raw';
import { getHotelConfig, setEnvVars } from './constants';
import { deepMerge, strToDate } from './utils';
import App from './App';

const defaultSelector = 'script#selfbook_searchbar';
const defaultSelectorAlt = 'script#sb_searchbar';

(() => {
  window.renderWidget = function renderWidget(selectorProp, configProps = null) {
    let selector = selectorProp || defaultSelector;
    let scriptTag = document.querySelector(selector);

    if (!scriptTag && document.querySelector(defaultSelectorAlt)) {
      selector = defaultSelectorAlt;
      scriptTag = document.querySelector(defaultSelectorAlt);
    }

    if (!scriptTag || scriptTag?.tagName?.toLowerCase() !== 'script') {
      if (selectorProp) {
        console.error(`Selfbook Search Widget initialization failed: ${selector} not found.`); 
      }
      return;
    }

    let scriptTagConfig = {};
    try {
      scriptTagConfig = scriptTag.textContent ? eval(`(${scriptTag.textContent})`) : {};
    } catch (e) {
      console.error(`Failed to parse Javascript in ${selector}:`, e);
    }

    let config = deepMerge(
      defaultConfig,
      { hotelPreselected: !!scriptTagConfig?.hotelId },
      configProps,
      scriptTagConfig,
    );

    config = deepMerge(
      config,
      getHotelConfig(config?.hotelId) || null,
    );

    // Add Google Font @font-face link tag
    if (!document.head.querySelector('#selfbook_searchbar_fontface') && !config.fontFamily) {
      // Create link element for Google Fonts
      const fontFace = document.createElement('link');
      fontFace.id = 'selfbook_searchbar_fontface';
      fontFace.setAttribute('rel', 'stylesheet');
      fontFace.setAttribute('href', 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap');
      document.head.appendChild(fontFace);
    }

    const { styles: configStyles, ...configRest } = config;
    useWidgetStore.getState().setConfig(configRest);
    if (config.theme) {
      useWidgetStore.getState().setTheme(config.theme);
    }
    if (config.fontFamily) {
      useWidgetStore.getState().setThemeFont(config.fontFamily);
    }
    if (config.primaryColor) {
      useWidgetStore.getState().setThemeColor(config.primaryColor);
    }
    if (config.backendURL) {
      setEnvVars({ backendURL: config.backendURL });
    }
    if (config.calendar?.startDate && config.calendar?.endDate) {
      useWidgetStore.getState().setCalendar([
        strToDate(config.calendar?.startDate),
        strToDate(config.calendar?.endDate),
      ]);
    }
    if (config.popoutDirection) {
      useWidgetStore.getState().setPopoutDirection(config.popoutDirection);
    }
    if (config.detectPopoutDirection) {
      useWidgetStore.getState().setDetectPopoutDirection(config.detectPopoutDirection);
    }
    if (window?.location?.search) {
      useWidgetStore.getState().setUrlParams(
        Object.fromEntries(new URLSearchParams(window.location.search).entries())
      );
    }
    useWidgetStore.getState().fetchHotelData();
    useWidgetStore.getState().fetchHotelInfo();

    const host = document.createElement('div');
    host.id = scriptTag.id || `selfbook_searchbar_${Math.random().toString(36).slice(2)}`;
    const shadowRoot = host.attachShadow({ mode: 'open' });
    scriptTag.parentNode.replaceChild(host, scriptTag);

    const CustomStyles = createGlobalStyle`
      ${globalStyles}
      ${config.fontFamily && `
        :host {
          --font-family: ${config.fontFamily};
        }
      `}
      ${scrollregionStyles}
      ${configStyles || ''}
    `;

    const emotionCache = createCache({
      key: 'shadow',
      container: shadowRoot,
    });

    const mountPoint = document.createElement('div');
    shadowRoot.appendChild(mountPoint);

    const root = ReactDOM.createRoot(mountPoint);
    root.render(
      <React.StrictMode>
        <CacheProvider value={emotionCache}>
          <StyleSheetManager target={shadowRoot}>
            <CustomStyles />
            <App />
          </StyleSheetManager>
        </CacheProvider>
      </React.StrictMode>
    );
  };
})();
